/* Reuses Broadcast's media selection event; tracks native scrolling and variant changes. */
if (!customElements.get('vieux-gallery-selectors')) {
  customElements.define('vieux-gallery-selectors', class extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('product-images');
      this.list = this.gallery?.querySelector('[data-product-media-list]');
      this.slides = [...(this.list?.querySelectorAll('[data-media-id][data-image-id]') || [])];
      this.buttons = [...this.querySelectorAll('[data-vieux-media-id]')];
      if (!this.list || this.slides.length < 2) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-vieux-media-id]');
        if (button && this.contains(button)) this.select(button.dataset.vieuxMediaId);
      }, options);
      this.list.addEventListener('scroll', () => this.scheduleSync(), { ...options, passive: true });
      window.addEventListener('resize', () => this.scheduleSync(), options);
      this.observer = new MutationObserver(() => this.setCurrent(this.gallery.dataset.activeMedia));
      this.observer.observe(this.gallery, { attributes: true, attributeFilter: ['data-active-media'] });
      this.setCurrent(this.gallery.dataset.activeMedia);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.selectionTimer);
    }

    isHorizontal() {
      const overflow = getComputedStyle(this.list).overflowX;
      return /auto|scroll/.test(overflow) && this.list.scrollWidth > this.list.clientWidth + 2;
    }

    isFader() {
      return this.gallery.hasAttribute(
        matchMedia('(max-width: 749px)').matches ? 'data-fader-mobile' : 'data-fader-desktop'
      );
    }

    setCurrent(id) {
      this.buttons.forEach(button => {
        button.setAttribute('aria-current', String(button.dataset.vieuxMediaId === id));
      });
    }

    select(id) {
      const slide = this.slides.find(item => item.dataset.mediaId === id);
      if (!slide) return;
      this.selectionUntil = performance.now() + 600;
      clearTimeout(this.selectionTimer);
      this.selectionTimer = setTimeout(() => this.scheduleSync(), 650);
      this.gallery.dispatchEvent(new CustomEvent('theme:media:select', { detail: { id } }));
      this.setCurrent(id);
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      if (this.isHorizontal()) {
        const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
        const left = this.list.scrollLeft + slide.getBoundingClientRect().left
          - this.list.getBoundingClientRect().left - inset;
        this.list.scrollTo({ left, behavior });
      } else if (!this.isFader()) {
        slide.scrollIntoView({ block: 'start', behavior });
      }
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.syncVisible();
      });
    }

    syncVisible() {
      if (this.isFader() || performance.now() < (this.selectionUntil || 0)) return;
      if (!this.isHorizontal()) return;
      const listBounds = this.list.getBoundingClientRect();
      // Keep the last selection while the whole gallery is outside the viewport.
      if (listBounds.bottom <= 0 || listBounds.top >= innerHeight) return;
      let nearest = null;
      let distance = Infinity;
      const inset = parseFloat(getComputedStyle(this.list).scrollPaddingLeft) || 0;
      this.slides.forEach(slide => {
        const bounds = slide.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        const next = Math.abs(bounds.left - listBounds.left - inset);
        if (next < distance) { distance = next; nearest = slide; }
      });
      if (!nearest) return;
      const id = nearest.dataset.mediaId;
      this.setCurrent(id);
      if (this.gallery.dataset.activeMedia !== id) {
        this.gallery.dispatchEvent(new CustomEvent('theme:media:select', { detail: { id } }));
      }
    }
  });
}
